// hooks
import { Fragment, useEffect, useState, useCallback } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import useHttp from "hooks/use-http";

// routes
import { useParams, useNavigate } from "react-router-dom";

// material styles
import { Grid, Button, TextField } from "@mui/material";

// material icons
import CheckIcon from "@mui/icons-material/Check";

// react icons
import { MdDelete } from "react-icons/md";

// components
import LoadingCM from "components/shared/Loading";
import DrawerCM from "components/shared/Drawer";
import BackButton from "components/shared/BackButton";
import DeleteModal from "components/shared/Modal/Delete";
import ReactHookFormSelect from "components/shared/ReactHookFormSelect";

// helper
import Toast from "helper/toast";

// form validate
import { serviceSubCategorySchema } from "validations";

// styled component
import {
  StyledStack,
  StyledTitle,
  ButtonContainer,
  StyledMenuItem,
} from "./editSubCategory.style";

// request methods
import {
  deleteServiceSubCategoryByIdRequest,
  editServiceSubCategoryRequest,
  getServiceSubcategoryByIdRequest,
} from "api/requests/services";

// services categories...
const SERVICES_CATEGORIES = [
  { value: "consultation", title: "مشاوره" },
  { value: "performance", title: "اجرا" },
  { value: "supervision", title: "نظارت" },
  { value: "export-import-clearance", title: "صادرات واردات و ترخیص" },
];

export default function EditServicesSubCategory() {
  const { services_subcategory_id } = useParams();

  const navigate = useNavigate();

  const { request, loading, setLoading } = useHttp(false);

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(serviceSubCategorySchema),
  });

  // selected category state
  const [selectedCategory, setSelectedCategory] = useState("");

  // show modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // show modal handler
  const handelShowModal = () => setShowDeleteModal((state) => !state);

  // delete services subcategory
  const handleDeleteItem = useCallback(
    async (id) => {
      const res = await request({
        url: () => deleteServiceSubCategoryByIdRequest(id),
      });
      if (res) {
        Toast({ type: "success", title: "حذف زیردسته خدمات " });
        navigate("/services/subcategory/add");
      }
      handelShowModal();
    },
    [setLoading]
  );

  // get subcategory data
  const getSubCategoryData = useCallback(async () => {
    setLoading(true);

    const res = await request({
      url: () =>
        getServiceSubcategoryByIdRequest({ id: services_subcategory_id }),
    });

    setLoading(false);
    if (res) {
      setSelectedCategory(res.data.category);

      let [categoryDetail] = SERVICES_CATEGORIES.filter(
        (item) => item.value === res.data.category
      );

      reset({
        title: res.data.title,
        category: categoryDetail.value,
      });
    }
    setLoading(false);
  }, [request]);

  // edit subcategory
  const onSubmit = async (data) => {
    const res = await request({
      url: () =>
        editServiceSubCategoryRequest({
          ...data,
          category: selectedCategory,
          id: services_subcategory_id,
        }),
    });

    if (res) {
      reset({
        title: "",
        category: "",
      });
      Toast({ type: "success", title: "ویرایش زیردسته " });
      navigate("/services/subcategory/add");
    }
    setLoading(false);
  };

  useEffect(() => {
    getSubCategoryData();
  }, [getSubCategoryData]);

  return (
    <DrawerCM title="ویرایش زیردسته محصولات">
      {loading ? (
        <LoadingCM />
      ) : (
        <Fragment>
          <Grid container>
            <Grid item mt={3} xs={12}>
              <BackButton Route="/services" />
            </Grid>
            <StyledStack>
              <Grid>
                <StyledTitle>ویرایش زیردسته </StyledTitle>
                <Grid container>
                  <Grid item md={6} xs={12} p={1.5}>
                    <TextField
                      label="عنوان زیردسته"
                      variant="outlined"
                      fullWidth
                      id="title"
                      {...register("title")}
                      error={Boolean(errors.title)}
                      helperText={errors.title?.message}
                    />
                  </Grid>
                  <Grid item md={6} xs={12} p={1.5}>
                    <ReactHookFormSelect
                      label="سردسته"
                      control={control}
                      title="سردسته"
                      fullWidth
                      id="category"
                      {...register("category")}
                      error={Boolean(errors.category)}
                      helperText={errors.category?.message}
                      onChange={(value) => setSelectedCategory(value)}
                    >
                      {SERVICES_CATEGORIES.map((item, index) => (
                        <StyledMenuItem key={index} value={item.value}>
                          {item.title}
                        </StyledMenuItem>
                      ))}
                    </ReactHookFormSelect>
                  </Grid>
                  <ButtonContainer item xs={12}>
                    <Button
                      color="success"
                      startIcon={<CheckIcon />}
                      variant="contained"
                      sx={{ mt: 1, mr: 1 }}
                      onClick={handleSubmit(onSubmit)}
                    >
                      ثبت
                    </Button>
                    <Button
                      color="error"
                      endIcon={<MdDelete />}
                      variant="contained"
                      sx={{ mt: 1, mr: 1 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handelShowModal();
                      }}
                    >
                      حذف
                    </Button>
                  </ButtonContainer>
                </Grid>
              </Grid>
            </StyledStack>
          </Grid>
          <DeleteModal
            show={showDeleteModal}
            onClose={handelShowModal}
            onDelete={handleDeleteItem.bind(null, services_subcategory_id)}
          />
        </Fragment>
      )}
    </DrawerCM>
  );
}
